import { useState, type ReactNode } from 'react'

import ChartHeader from '@/components/charts/ChartHeader'
import Sparkline from '@/components/charts/Sparkline'
import Stagger from '@/components/motion/Stagger'
import PeriodTabs, { type PeriodOption } from '@/components/ui/PeriodTabs'
import type { SegmentOption } from '@/components/ui/SegmentedControl'
import Change from '@/components/ui/Change'
import StockAvatar from '@/components/ui/StockAvatar'
import type { MarketRange } from '@/features/market/api/marketApi'
import { usePriceHistory } from '@/features/prices/api/queries'
import SegmentToggle from '@/features/screener/components/SegmentToggle'
import SymbolTarget from '@/features/screener/components/SymbolTarget'
import { formatMetric } from '@/features/screener/lib/format'
import { pctChange, sign, toChartNumber } from '@/lib/money'
import { cn } from '@/lib/utils'
import type { Metric, ScreenerRow } from '@/schemas/screener'

/**
 * The mock's 1M / 3M / 6M / 1Y, less 6M: the price-history read serves 1W, 1M, 3M, 1Y and all,
 * so a six-month card would be a range nothing draws.
 */
type ChartRange = Extract<MarketRange, '1M' | '3M' | '1Y'>

const RANGES: readonly PeriodOption<ChartRange>[] = [
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '1Y', label: '1Y' },
]

type ChartStyle = 'line' | 'area'

const STYLES: readonly SegmentOption<ChartStyle>[] = [
  { value: 'line', label: 'Line' },
  { value: 'area', label: 'Area' },
]

interface ScreenerChartViewProps {
  rows: readonly ScreenerRow[]
  /** The price metric, for the card's figure in its unit's words. */
  price: Metric | undefined
  /** The view menu, at the right end of the bar as on every view. */
  viewMenu: ReactNode
  onOpenSymbol?: ((symbol: string) => void) | undefined
}

/**
 * Chart (the mock's `Wpe`): a card per stock on the page, each the stock's own closes over the
 * chosen range from the app's per-stock price history, and its change over that range read off
 * the same series. The range and Line / Area sit right in the chart head (owner, chart actions
 * right), with the view menu.
 */
export default function ScreenerChartView({
  rows,
  price,
  viewMenu,
  onOpenSymbol,
}: ScreenerChartViewProps) {
  const [range, setRange] = useState<ChartRange>('3M')
  const [style, setStyle] = useState<ChartStyle>('area')

  return (
    <div className="@container flex flex-col gap-3">
      <ChartHeader
        toggle={
          <SegmentToggle
            options={STYLES}
            value={style}
            onChange={setStyle}
            ariaLabel="Chart style"
            className="w-36"
          />
        }
        action={viewMenu}
        periods={
          <PeriodTabs
            options={RANGES}
            value={range}
            onChange={setRange}
            ariaLabel="Range"
            variant="boxed"
          />
        }
      />
      {/* One card a row, two from 512 of column, three from 768 (the mock's 480 / 760). */}
      <Stagger as="ul" className="grid grid-cols-1 gap-3 @lg:grid-cols-2 @3xl:grid-cols-3">
        {rows.map((row) => (
          <li key={row.symbol} className="min-w-0">
            <ChartCard
              row={row}
              price={price}
              range={range}
              area={style === 'area'}
              onOpenSymbol={onOpenSymbol}
            />
          </li>
        ))}
      </Stagger>
    </div>
  )
}

function ChartCard({
  row,
  price,
  range,
  area,
  onOpenSymbol,
}: {
  row: ScreenerRow
  price: Metric | undefined
  range: ChartRange
  area: boolean
  onOpenSymbol: ((symbol: string) => void) | undefined
}) {
  const history = usePriceHistory(row.symbol, range)
  const points = history.data?.points ?? []
  const first = points[0]?.value
  const last = points[points.length - 1]?.value
  const change =
    first !== undefined && last !== undefined && sign(first) > 0 ? pctChange(first, last) : null
  const ltp = row.values['ltp'] ?? null

  return (
    <SymbolTarget
      symbol={row.symbol}
      onOpenSymbol={onOpenSymbol}
      className={cn(
        'group/card flex w-full cursor-pointer flex-col gap-3 text-left border border-border-subtle bg-card px-4 py-4 text-foreground',
        'transition-[border-color,translate] duration-fast hover:border-border-strong motion-safe:hover:-translate-y-1',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
      )}
    >
      <span className="flex min-w-0 items-center gap-3">
        <StockAvatar
          symbol={row.symbol}
          name={row.name}
          monogram={row.symbol.slice(0, 2)}
          size={28}
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-h-s underline-offset-3 group-hover/card:underline">
            {row.symbol}
          </span>
          <span className="truncate text-body-xs text-muted-foreground-subtle" title={row.name}>
            {row.name}
          </span>
        </span>
        <span className="text-h-s tnum">
          {ltp === null || price === undefined ? '—' : formatMetric(price, ltp)}
        </span>
      </span>
      <Sparkline
        points={points.map((point) => toChartNumber(point.value))}
        width={240}
        height={64}
        stretch
        area={area}
        endDot={false}
        strokeWidth={1.5}
        state={history.isError ? 'failed' : history.data === undefined ? 'loading' : 'loaded'}
        {...(change === null ? {} : { direction: sign(change) < 0 ? 'down' : 'up' })}
        className="h-16 w-full"
      />
      <span className="flex justify-between text-body-xs">
        <span className="text-muted-foreground-subtle">Change over {range}</span>
        <Change value={change} percent className="font-semibold" />
      </span>
    </SymbolTarget>
  )
}
